import { Injectable, signal } from '@angular/core';
import {
  ById,
  ExtractedDataSnapshot,
  ExtractedTablesByKey,
  ExtractedValuesByKey,
} from '../../models/extracted-values';
import { ExtractedFieldLocations, SplitEntity } from '../../models/interfaces';
import { clear, get, set } from 'idb-keyval';
import { TableData } from '../../table-overlay/table-overlay';
import { mapValues } from '@ps-gui/shared-utils';
import { debounceTime, groupBy, mergeMap, Subject, switchMap } from 'rxjs';
import { DeepUnsignal } from '@ps-gui/shared-core';
import { DocumentClassKey } from '@ps-gui/claims-terminal-shared';
import { BehandlerListEntry } from '../../pages/erfassung-behandler-page/components/model/behandler-list-entry.interface';
import { AltfallZuweisung } from '../../models/altfall-zuweisung';

export type RawTableData = DeepUnsignal<TableData>;

export const mapRawToApplicationTableData = (rawTableData: RawTableData): TableData => {
  return {
    ...rawTableData,
    layoutDefined: signal(rawTableData.layoutDefined),
    columnHeaders: signal(rawTableData.columnHeaders),
    confirmed: signal(rawTableData.confirmed),
    lines: {
      ...mapValues(rawTableData.lines, (_, lines) =>
        signal(
          lines.map((line) => ({
            ...line,
            offset: signal(line.offset),
          })),
        ),
      ),
    },
  };
};

export const mapApplicationToRawTableData = (tableData: TableData): RawTableData => {
  return {
    ...tableData,
    layoutDefined: tableData.layoutDefined(),
    columnHeaders: tableData.columnHeaders(),
    confirmed: tableData.confirmed(),
    lines: {
      ...mapValues(tableData.lines, (_, lines) =>
        lines().map((line) => ({
          ...line,
          offset: line.offset(),
        })),
      ),
    },
  };
};

export interface ExtractedDocumentDataSnapshot {
  fields: ExtractedValuesByKey;
  tables: ExtractedTablesByKey;
}

interface PersistTablesEvent {
  documentId: string;
  tables: ExtractedTablesByKey;
}

@Injectable({
  providedIn: 'root',
})
// should not be used in components, only in services to access localStorage
export class PersistDataService {
  private readonly tableSaveEvents = new Subject<PersistTablesEvent>();

  constructor() {
    this.tableSaveEvents
      .pipe(
        groupBy((event) => event.documentId),
        mergeMap((events) =>
          events.pipe(
            debounceTime(1000),
            switchMap(({ documentId, tables }) => this.persistTables(documentId, tables)),
          ),
        ),
      )
      .subscribe();
  }

  get ctBatchId(): string | null {
    return this.getLocalStorageValue('ctBatchId', null);
  }

  async setCtBatchId(ctBatchId: string) {
    this.setLocalStorageValue('ctBatchId', ctBatchId);
  }

  private setIndexDbValue(key: string, value: unknown): Promise<void> {
    return set(key, JSON.stringify(value));
  }

  private async getIndexDbValue<T>(key: string, defaultValue: T | null = null): Promise<T | null> {
    const value = (await get(key)) as string;
    return value ? (JSON.parse(value) as T) : defaultValue;
  }

  private setLocalStorageValue(key: string, value: unknown) {
    localStorage.setItem(key, JSON.stringify(value));
  }

  private getLocalStorageValue<T>(key: string, defaultValue: T | null = null): T | null {
    const value = localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : defaultValue;
  }

  set extractedFields(fields: ById<ExtractedValuesByKey>) {
    this.setLocalStorageValue('extractedFields', fields);
  }

  get extractedFields(): ById<ExtractedValuesByKey> | null {
    return this.getLocalStorageValue('extractedFields');
  }

  set behandlerFormData(data: BehandlerListEntry) {
    this.setLocalStorageValue('behandlerFormData', data);
  }

  get behandlerFormData(): BehandlerListEntry | null {
    return this.getLocalStorageValue('behandlerFormData');
  }

  set behandlerData(data: BehandlerListEntry[]) {
    this.setLocalStorageValue('behandlerData', data);
  }

  get behandlerData(): BehandlerListEntry[] {
    return this.getLocalStorageValue('behandlerData') ?? [];
  }

  get highestBehandlerIndex(): number {
    return Number.parseInt(this.getLocalStorageValue('highestBehandlerIndex') ?? '0');
  }

  set highestBehandlerIndex(value: number) {
    this.setLocalStorageValue('highestBehandlerIndex', value);
  }

  get altfallZuweisung(): AltfallZuweisung | null {
    return this.getLocalStorageValue('altfallZuweisung');
  }

  set altfallZuweisung(value: AltfallZuweisung | null) {
    this.setLocalStorageValue('altfallZuweisung', value);
  }

  addPersistTablesEvent(documentId: string, tables: ExtractedTablesByKey) {
    this.tableSaveEvents.next({ documentId, tables });
  }

  getExtractedTables(documentId: string): Promise<ExtractedTablesByKey | null> {
    return this.getIndexDbValue(`extracted-tables-${documentId}`);
  }

  set tableDataByPageId(tableDataByPageId: Record<string, RawTableData[]>) {
    this.setLocalStorageValue('tableDataByPageId', tableDataByPageId);
  }

  get tableDataByPageId(): Record<string, RawTableData[]> | null {
    return this.getLocalStorageValue('tableDataByPageId');
  }

  set extractedLocationsByDocumentId(extractedFieldLocations: ById<ExtractedFieldLocations>) {
    this.setLocalStorageValue('extractedFieldLocations', extractedFieldLocations);
  }

  get extractedLocationsByDocumentId(): ById<ExtractedFieldLocations> | null {
    return this.getLocalStorageValue('extractedFieldLocations');
  }

  get currentViewIndex(): number | null {
    return this.getLocalStorageValue('currentViewIndex');
  }

  set currentViewIndex(currentViewIndex: number) {
    this.setLocalStorageValue('currentViewIndex', currentViewIndex);
  }

  flagExistingManualChanges(documentId: string) {
    this.setLocalStorageValue(`manual-changes-${documentId}`, true);
  }

  doManualChangesExist(documentId: string): boolean {
    return this.getLocalStorageValue<boolean>(`manual-changes-${documentId}`) ?? false;
  }

  setUseTrainingDataForDocument(documentId: string, shouldUse: boolean) {
    this.setLocalStorageValue(`use-training-data-${documentId}`, shouldUse);
  }

  shouldUseTrainingDataForDocument(documentId: string): boolean {
    return !!this.getLocalStorageValue(`use-training-data-${documentId}`);
  }

  setSplitEntities(splitEntities: SplitEntity[]) {
    this.setLocalStorageValue('split-entities', splitEntities);
  }

  getSplitEntities(): SplitEntity[] {
    return this.getLocalStorageValue<SplitEntity[]>('split-entities') ?? [];
  }

  async saveExtractedDocumentDataSnapshot(
    documentId: string,
    documentClass: string,
    snapshot: ExtractedDocumentDataSnapshot,
  ) {
    return this.setIndexDbValue(
      `extraction-data-snapshot-${documentId}-${documentClass}`,
      snapshot,
    );
  }

  async getExtractedDocumentDataSnapshot(
    documentId: string,
    documentClass: DocumentClassKey,
  ): Promise<ExtractedDocumentDataSnapshot> {
    return (
      (await this.getIndexDbValue<ExtractedDataSnapshot>(
        `extraction-data-snapshot-${documentId}-${documentClass}`,
      )) ?? { fields: {}, tables: {} }
    );
  }

  async saveConsolidatedDataSnapshot(
    consolidationIndex: number,
    fields: Partial<ExtractedValuesByKey>,
    tables: Partial<ExtractedTablesByKey>,
  ) {
    return this.setIndexDbValue(`consolidated-data-snapshot-${consolidationIndex}`, {
      fields,
      tables,
    });
  }

  async getConsolidatedDataSnapshot(consolidationIndex: number) {
    return (
      (await this.getIndexDbValue<ExtractedDataSnapshot>(
        `consolidated-data-snapshot-${consolidationIndex}`,
      )) ?? { fields: {}, tables: {} }
    );
  }

  reset(): Promise<void> {
    localStorage.clear();
    return clear();
  }

  async persistTables(documentId: string, tablesByKey: ExtractedTablesByKey): Promise<void> {
    return this.setIndexDbValue(`extracted-tables-${documentId}`, tablesByKey);
  }
}
